import React from 'react';
import { Text } from '@oneaudi/unified-web-components';
import { CardWrapper, HeadingWrapper, StyledImage, StyledTagContainer } from './CarGroupStyles';
import type { MoxxCarlineGroup } from '../../../@types/MoxxTypes';
import { useAppContext } from '../../context/Context';
import { ArrowIcon } from '../ArrowIcon';
import { getOptimizedImageSrc } from '../../utils/imgUtils';
import { useTrackingManager } from '../../context/useTrackingManager';
import {
  IMAGE_MIME_TYPE,
  CARGROUP_IMAGE_HEIGHT,
  CARGROUP_IMAGE_WIDTH,
  CARGROUP_SCALED_IMAGE_WIDTH,
} from '../../constants';
import { Layout, Tag, Tile } from '@oneaudi/unified-web-alpha-components';
import styled from 'styled-components';
import { VehicleType } from '../../../web/generated/onegraph';

interface CarGroupCardProps {
  carlineGroup: MoxxCarlineGroup;
  isSelected: boolean;
  onClick: () => void;
  onFocus?: () => void;
  amountCarlines: string;
  dataTestId: string;
}

const ArrowWrapper = styled.span`
  color: ${({ theme }) => theme.cmp.color.navigation.textLink.foreground.default};
`;

const CarGroupCard: React.ForwardRefRenderFunction<HTMLLIElement, CarGroupCardProps> = (
  { carlineGroup, isSelected, onClick, amountCarlines, dataTestId },
  ref,
) => {
  const { selectedCarGroupId, selectedFilter } = useAppContext();
  const cardImageSrc = getOptimizedImageSrc(carlineGroup.carlines[0].image, {
    extension: IMAGE_MIME_TYPE,
    width: '180',
  });
  // eslint-disable-next-line @typescript-eslint/unbound-method
  const { trackExpandCardClick } = useTrackingManager();
  const hasElectrical = carlineGroup.carlines.some((carline) =>
    carline.vehicleType?.includes(VehicleType.Bev.toString()),
  );

  const copySize = process.env.IS_LEGACY ? '2' : '3';

  return (
    <CardWrapper key={carlineGroup.id} ref={ref}>
      <Tile
        aria-label={carlineGroup.name}
        onClick={() => {
          onClick();
          trackExpandCardClick(carlineGroup.name, carlineGroup.id, 'image', selectedFilter);
        }}
        data-trigger={true}
        selected={isSelected}
      >
        <span data-testid={dataTestId}>
          <HeadingWrapper>
            <Text
              tag="span"
              variant={{
                type: 'copy',
                order: '1',
                style: 'normal',
              }}
            >
              {carlineGroup.name}
            </Text>
            {hasElectrical && (
              <StyledTagContainer>
                <Tag icon="charging" size="sm" variant="read-only" label="" />
              </StyledTagContainer>
            )}
          </HeadingWrapper>
          <StyledImage
            src={getOptimizedImageSrc(cardImageSrc, { width: CARGROUP_SCALED_IMAGE_WIDTH })}
            alt={carlineGroup.name}
            width={CARGROUP_IMAGE_WIDTH}
            height={CARGROUP_IMAGE_HEIGHT}
            srcSet={`${getOptimizedImageSrc(cardImageSrc, { width: CARGROUP_SCALED_IMAGE_WIDTH })} ${CARGROUP_IMAGE_WIDTH}w, ${getOptimizedImageSrc(cardImageSrc, { width: CARGROUP_SCALED_IMAGE_WIDTH * 2 })} ${CARGROUP_IMAGE_WIDTH * 2}w`}
            sizes={`${CARGROUP_IMAGE_WIDTH}px`}
            loading="lazy"
          />
          <Layout as="span" justify="space-between" align="center">
            <Text tag="span" variant={{ type: 'copy', order: copySize, style: 'normal' }}>
              {amountCarlines}
            </Text>
            <ArrowWrapper>
              <ArrowIcon rotated={carlineGroup.id === selectedCarGroupId} />
            </ArrowWrapper>
          </Layout>
        </span>
      </Tile>
    </CardWrapper>
  );
};
export default React.forwardRef(CarGroupCard);
